import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { PatientPortalProvider } from './context/PatientPortalContext';
import { PatientChartProvider } from './context/PatientChartContext';
import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminOnlyRoute from './components/AdminOnlyRoute';

// Import pages
import LoginPage from './pages/LoginPage';
import { initializeGlobalObjectRenderFix } from './utils/globalObjectRenderFix';
import DashboardPage from './pages/DashboardPage';
import PatientListPage from './pages/PatientListPage';
import PatientDetailPage from './pages/PatientDetailPage';
import AppointmentPage from './pages/AppointmentPage';
import UserManagementPage from './pages/UserManagementPage';
import ClinicalSettingsPage from './pages/ClinicalSettingsPage';
import LabManagementPage from './pages/LabManagementPage';
import PhenoAgeValidationPage from './pages/PhenoAgeValidationPage';
import PreventScoreToggleTest from './components/PreventScoreToggleTest';
import BiologicalAgeTest from './test/BiologicalAgeTest';
import MedlinePlusConnect from './components/MedlinePlusConnect';
import WorkBasketPage from './pages/WorkBasketPage';


// Import patient portal pages
import PatientPortalLoginPage from './pages/PatientPortalLoginPage';
import PatientPortalDashboard from './pages/PatientPortalDashboard';
import PatientIntakePortalPage from './pages/PatientIntakePortalPage';
import PatientIntakePortalPageSimple from './pages/PatientIntakePortalPageSimple';
import StaffPortalPreview from './pages/StaffPortalPreview';
import PublicSurveyPage from './pages/PublicSurveyPage';
import OriginalMedicalDashboardPage from './pages/OriginalMedicalDashboardPage';
import HealthOverviewDemo from './pages/HealthOverviewDemo';
import FigmaHealthOverviewDemo from './pages/FigmaHealthOverviewDemo';

import './App.css';

// Patient Portal App Component
const PatientPortalApp = () => {
  return (
    <LanguageProvider>
      <PatientPortalProvider>
        <Routes>
          <Route path="login" element={<PatientPortalLoginPage />} />
          <Route path="dashboard" element={<PatientPortalDashboard />} />
          <Route path="" element={<Navigate to="login" replace />} />
        </Routes>
      </PatientPortalProvider>
    </LanguageProvider>
  );
};

// Initialize global object render fix
initializeGlobalObjectRenderFix();

// Main App Component
function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1890ff',
        },
      }}
    >
      <LanguageProvider>
        <AuthProvider>
          <PatientChartProvider>
            <Router>
              <Routes>
                {/* Patient Portal Routes */}
                <Route path="/portal/*" element={<PatientPortalApp />} />

                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/intake" element={<PatientIntakePortalPage />} />
                <Route path="/intake-test" element={<PatientIntakePortalPageSimple />} />
                <Route path="/survey/:token" element={<PublicSurveyPage />} />

                {/* Standalone Medical Dashboard - No Ant Design */}
                <Route path="/original-dashboard" element={<OriginalMedicalDashboardPage />} />

                {/* Health Overview Demo */}
                <Route path="/health-overview-demo" element={
                  <ProtectedRoute>
                    <HealthOverviewDemo />
                  </ProtectedRoute>
                } />

                {/* Figma Health Overview Demo */}
                <Route path="/figma-health-overview-demo" element={
                  <ProtectedRoute>
                    <FigmaHealthOverviewDemo />
                  </ProtectedRoute>
                } />

                {/* Protected Main Application Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Navigate to="/dashboard" replace />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DashboardPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/patients" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PatientListPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/patients/:patientId" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PatientDetailPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/portal/staff-preview/:patientId" element={
                  <ProtectedRoute>
                    <StaffPortalPreview />
                  </ProtectedRoute>
                } />

                <Route path="/appointments" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AppointmentPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/work-basket" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <WorkBasketPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/user-management" element={
                  <ProtectedRoute>
                    <AdminOnlyRoute>
                      <MainLayout>
                        <UserManagementPage />
                      </MainLayout>
                    </AdminOnlyRoute>
                  </ProtectedRoute>
                } />

                <Route path="/clinical-settings" element={
                  <ProtectedRoute>
                    <AdminOnlyRoute>
                      <MainLayout>
                        <ClinicalSettingsPage />
                      </MainLayout>
                    </AdminOnlyRoute>
                  </ProtectedRoute>
                } />

                <Route path="/lab-management" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <LabManagementPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />



                <Route path="/phenoage-validation" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PhenoAgeValidationPage />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/test-prevent-toggles" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PreventScoreToggleTest />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/test-biological-age" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <BiologicalAgeTest />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                <Route path="/test-medlineplus" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <MedlinePlusConnect />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* Catch all route - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Router>
          </PatientChartProvider>
        </AuthProvider>
      </LanguageProvider>
    </ConfigProvider>
  );
}

export default App;
